import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Text } from '@thefarmersfront/kitchen';

import dayjs from 'dayjs';

import { formatNumber } from '~/utils/common';

import type { TypeDailyTrend } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyDeliveryStatusTrendTooltip.module.scss';

const TOOLTIP_GAP = 8;

const TOOLTIP_ROWS = [
  { key: 'nightAcceptedCount', label: '당일배송', color: '#4EB595' },
  { key: 'dawnAcceptedCount', label: '샛별배송', color: '#BD76FF' },
  { key: 'dayAcceptedCount', label: '하루배송(택배)', color: '#5094DC' },
  { key: 'delayedRate', label: '지연배송', color: '#F1C236' },
  { key: 'failedRate', label: '배송실패', color: '#E22D2E' },
] as const;

const formatValue = (key: string, value: number) => {
  if (key === 'delayedRate' || key === 'failedRate') {
    return `${value.toFixed(1)}%`;
  }
  return `${formatNumber(value)}건`;
};

interface DeliveryAgencyDeliveryStatusTrendTooltipProps {
  top: number;
  left: number;
  leftX: number;
  item: TypeDailyTrend;
}

const DeliveryAgencyDeliveryStatusTrendTooltip = ({
  top,
  left,
  leftX,
  item,
}: DeliveryAgencyDeliveryStatusTrendTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedLeft, setAdjustedLeft] = useState(left + TOOLTIP_GAP);
  const [adjustedTop, setAdjustedTop] = useState(top);

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;

    const { width, height } = tooltipRef.current.getBoundingClientRect();

    const overflowRight = left + TOOLTIP_GAP + width > window.innerWidth;
    setAdjustedLeft(overflowRight ? leftX - width - TOOLTIP_GAP : left + TOOLTIP_GAP);

    const overflowBottom = top + height + TOOLTIP_GAP > window.innerHeight;
    setAdjustedTop(overflowBottom ? window.innerHeight - height - TOOLTIP_GAP : top);
  }, [left, leftX, top]);

  const dateLabel = dayjs(item.deliveryDate).format('M월 D일');

  return createPortal(
    <div ref={tooltipRef} role="tooltip" className={styles.container} style={{ top: adjustedTop, left: adjustedLeft }}>
      <Text size="large" weight="semi-bold">
        {dateLabel}
      </Text>
      <div className={styles.rows}>
        {TOOLTIP_ROWS.map((row) => (
          <div key={row.key} className={styles.row}>
            <span className={styles.color} style={{ backgroundColor: row.color }} />
            <Text size="large" weight="regular">
              {row.label}:
            </Text>
            <Text size="large" weight="regular">
              {formatValue(row.key, item[row.key] ?? 0)}
            </Text>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export { DeliveryAgencyDeliveryStatusTrendTooltip };
